"use client";

import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { FEATURE_FLAGS } from "@/lib/feature-flags";

import { requestTour, useToursStore } from "../lib/tour-store";
import type { TourId } from "../lib/registry";

interface TourReplayButtonProps {
  /**
   * Tour to replay. Defaults to the tour registered by the screen currently on
   * view (via `useAutoStartTour`), so tab-based pages resolve automatically.
   */
  tourId?: TourId;
  className?: string;
}

/**
 * Feature-page control that replays the guided tour. Renders nothing unless
 * tours are enabled and a tour is available for the current screen. Meant to
 * sit alongside a page's primary actions (e.g. next to Share).
 */
export function TourReplayButton({ tourId, className }: TourReplayButtonProps) {
  const areaTourId = useToursStore((s) => s.areaTourId);
  const isActive = useToursStore((s) => s.activeTourId !== null);
  const target = tourId ?? areaTourId;

  if (!FEATURE_FLAGS.tours || !target) return null;

  return (
    <Button
      variant="secondary"
      size="sm"
      className={className ?? "hidden text-contrast/70 lg:inline-flex"}
      onClick={() => requestTour(target)}
      disabled={isActive}
      aria-label="Show the guided tour again"
      title="Show the guided tour again"
    >
      <Icon
        name="IconQuestionmarkCircle2"
        size={16}
        className="text-heading opacity-50"
      />
    </Button>
  );
}
